import React from 'react';

import { Box } from '@atlaskit/primitives/compiled';
import type { IconProps } from '@atlaskit/temp-nav-app-icons/types';
import Icon, { type Size as IconSize } from '@atlaskit/icon';

type Size = 'small' | 'medium' | 'large';

type CorrectableSize = 'xsmall' | 'small' | 'medium' | 'large';

const isCorrectableRovoSize = (size: string): size is CorrectableSize => {
	return ['xsmall', 'small', 'medium', 'large'].includes(size);
};

export interface SVGIconProps {
	glyph: () => JSX.Element;
	label?: string;
	size: Size;
}

export const SVGIcon = ({ glyph: SVG, label }: SVGIconProps) => {
	return (
		<Box aria-label={label}>
			<SVG />
		</Box>
	);
};

export type LogoProps = {
	label?: string;
	shouldUseNewLogoDesign?: boolean;
	size?: IconProps['size'];
	testId?: string;
};

export type Logo = (props: LogoProps) => JSX.Element;

export type LogoGlyphWrapper = (
	glyph: () => JSX.Element,
	defaultLabel?: string,
	size?: Size,
) => Logo;

export type LogoWrapper = (LogoComponent: any, defaultLabel?: string) => Logo;

/**
 * Due a bug in ADS custom icons are one size smaller (than atlaskit/logo)
 * To accommodate for this behavior you need to use one size bigger than what you need
 * See: https://atlassian.slack.com/archives/CFJ9DU39U/p1714096323217799
 */
const correctedSize: Record<CorrectableSize, IconSize> = {
	xsmall: 'small',
	small: 'medium',
	medium: 'large',
	large: 'xlarge',
};

/**
 * A wrapper for providing an internal \@atlaskit/icon - alike experience for working with custom Rovo Icons
 *
 * Internally uses <Icon glyph={glyph} ... />
 *
 * @param {} glyph function returning an SVG as JSX
 * @param defaultLabel an accessible label for the logo
 */
export const LogoFromGlyph: LogoGlyphWrapper = (glyph, defaultLabel = '', defaultSize?: Size) => {
	return ({ size = defaultSize, label = defaultLabel, testId }) => {
		return (
			// eslint-disable-next-line @atlaskit/design-system/no-custom-icons -- to be migrated to @atlaskit/icon/core – go/akicon
			<Icon
				label={label}
				testId={testId}
				glyph={glyph}
				size={size && isCorrectableRovoSize(size) ? correctedSize[size] : defaultSize}
			/>
		);
	};
};

/**
 * A wrapper for rendering 3P svg illustrations in a similar way for atlaskit icon components
 *
 * @param {} glyph function returning an SVG as JSX
 * @param defaultLabel an accessible label for the logo
 */
export const LogoFromSVG: LogoGlyphWrapper = (glyph, defaultLabel = '', size) => {
	return ({ label = defaultLabel, testId }) => (
		<SVGIcon label={label} glyph={glyph} size={size || 'medium'} />
	);
};

export function get3PLogo(iconGlyph: () => JSX.Element, iconLabel: string, size?: Size): Logo {
	return LogoFromSVG(iconGlyph, iconLabel, size);
}

/**
 * A wrapper for providing an internal \@atlaskit/icon - alike experience for working with \@atlaskit/logo
 *
 * Internally uses <LogoComponent ... />
 *
 * @param {} LogoComponent an import from \@atlaskit/logo
 * @param defaultLabel an accessible label for the logo
 */
export const LogoFromLogo: LogoWrapper = (LogoComponent, defaultLabel = '') => {
	return ({ size, label, testId }) => {
		return (
			<LogoComponent
				label={label || defaultLabel}
				testId={testId}
				size={size || 'medium'}
				appearance="brand"
				shouldUseNewLogoDesign={true}
			/>
		);
	};
};

/**
 * Takes a logo component and returns a new logo component that uses the new logo design.
 */
export function IconFromIcon(Icon: (props: LogoProps) => React.ReactNode) {
	return (props: LogoProps) => <Icon {...props} shouldUseNewLogoDesign={true} />;
}
